import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import scrollbar from '../../styles/scrollbar';
import RobotIcon from '../icons/RobotIcon';
import Avatar from './Avatar';
import KickButton from './KickButton';
import { useT } from '../../i18n';

const ROBOT_COLORS = ['#d94f2b', '#2a63a8', '#e0a325', '#2f9e6e'];
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Player list — one pressed plate per player.
 *
 * Rank numeral · robot mark (the player's own colour) · name · state badge ·
 * score in a recessed well. The leader's well gets a mustard bezel; the player
 * currently demonstrating gets a tomato ring on their mark.
 *
 * Colour is assigned by the player's index in the unsorted `players` array, so
 * it stays stable as the scoreboard re-sorts.
 *
 * The host also gets a KickButton on every row but their own — the server
 * enforces both halves of that rule, this is only the affordance.
 *
 * The rows scroll inside their own well rather than growing without limit.
 * The sidebar is a flex column, and an unbounded player list is what pushes
 * everything under it — the event log, and the lobby's Start Game — out of
 * the panel. `Players` and its count stay outside the scroller, so the
 * heading does not scroll away from the list it names.
 *
 * In LOBBY one open row sits under the players, so the lobby says what it is
 * waiting for. Deliberately ONE, and with no number anywhere near it: this
 * game has no player cap — the server seats a fifth player and hands them a
 * colour already in use — so a fixed set of empty chairs, or a `n / 4` count,
 * would advertise a limit that does not exist.
 */
const useStyles = createUseStyles({
  // minHeight 0 lets the panel squeeze this below its content instead of
  // overflowing the sidebar; `list` is what absorbs the squeeze.
  container: { display: 'flex', flexDirection: 'column', gap: '7px', minHeight: 0 },
  // A row is 42px plus the 7px gap, so the cap is about six players before it
  // scrolls — and less than that on a short window, where the rest of the
  // panel needs the room more.
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    minHeight: 0,
    maxHeight: 'min(40vh, 294px)',
    overflowY: 'auto',
    ...scrollbar,
  },
  head: { display: 'flex', alignItems: 'center', gap: '8px' },
  label: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#c4a070',
  },
  rule: { flex: 1, height: '1px', background: '#3a2718' },
  count: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: '#7d6549',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '8px 10px 8px 8px',
    background: '#2e1c0d',
    borderRadius: '8px',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 1px 0 rgba(255,255,255,0.07)',
      'inset 0 -3px 0 rgba(0,0,0,0.3)',
    ].join(','),
  },
  rowSelf: {
    background: '#33200f',
    boxShadow: [
      'inset 0 0 0 1px #5c3a1e',
      'inset 0 1px 0 rgba(255,255,255,0.10)',
      'inset 0 -3px 0 rgba(0,0,0,0.34)',
    ].join(','),
  },
  rowIdle: { opacity: 0.62 },
  rank: {
    width: '14px',
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    color: '#7d6549',
    textAlign: 'right',
  },
  rankLead: { color: '#e0a325' },
  mark: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: '#3a2416',
    boxShadow: 'inset 0 0 0 1.5px #120c07, inset 0 1px 0 rgba(255,255,255,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  markActive: { boxShadow: 'inset 0 0 0 1.5px #120c07, 0 0 0 2px #d94f2b' },
  markInner: { width: '21px', height: '21px' },
  name: {
    flex: 1,
    minWidth: 0,
    fontSize: '14.5px',
    color: '#e8d6b8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameSelf: { color: '#f5e6c8' },
  badge: {
    fontFamily: MONO,
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: '#f0dcc0',
    background: '#7d2a12',
    padding: '2px 5px',
    borderRadius: '3px',
    flexShrink: 0,
  },
  bid: {
    fontFamily: MONO,
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: '#e0a325',
    flexShrink: 0,
  },
  score: {
    minWidth: '30px',
    padding: '3px 0',
    textAlign: 'center',
    fontFamily: MONO,
    fontSize: '14px',
    fontWeight: 700,
    color: '#e8d6b8',
    background: '#1a0f07',
    borderRadius: '5px',
    boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.6)',
    flexShrink: 0,
  },
  scoreLead: {
    color: '#f6ead6',
    boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.6), inset 0 0 0 1px #e0a325',
  },
  // Same padding and mark size as `row`, so the two line up without either
  // hardcoding a height, but no plate fill — an open row is an outline, not a
  // player.
  seat: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '8px 10px 8px 8px',
    borderRadius: '8px',
    boxShadow: 'inset 0 0 0 1px #3a2718',
  },
  seatRing: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: 'inset 0 0 0 1.5px #3a2718',
  },
  seatLabel: {
    flex: 1,
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    // 3.9:1 on the panel. Do not darken to #6b5438 — that is 2.5:1.
    color: '#8a6e4c',
  },
});

function ScoreBoard({
  players = [], scores = {}, selfId, hostId, demoPlayerId, bids = [], phase, onKickPlayer,
}) {
  const classes = useStyles();
  const t = useT();
  const canKick = Boolean(onKickPlayer) && selfId === hostId;

  const colorOf = useMemo(() => {
    const map = {};
    players.forEach((p, i) => { map[p.id] = ROBOT_COLORS[i % ROBOT_COLORS.length]; });
    return map;
  }, [players]);

  const sorted = useMemo(
    () => [...players].sort((a, b) => (scores[b.id] ?? 0) - (scores[a.id] ?? 0)),
    [players, scores],
  );

  const topScore = sorted.length ? (scores[sorted[0].id] ?? 0) : 0;

  // LOBBY-only: once the round is running, an empty row is noise.
  const isLobby = phase === 'LOBBY';

  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <span className={classes.label}>{t('game.players')}</span>
        <span className={classes.rule} />
        <span className={classes.count}>{String(players.length).padStart(2, '0')}</span>
      </div>

      <div className={classes.list}>
        {sorted.map((player, i) => {
          const score = scores[player.id] ?? 0;
          const isSelf = player.id === selfId;
          const isLead = score > 0 && score === topScore;
          const isDemo = player.id === demoPlayerId;
          const bid = bids.find(b => b.id === player.id || b.playerId === player.id);
          const color = colorOf[player.id] ?? ROBOT_COLORS[0];

          return (
            <div
              key={player.id}
              className={[
                classes.row,
                isSelf ? classes.rowSelf : '',
                score === 0 && !isSelf ? classes.rowIdle : '',
              ].join(' ')}
            >
              <span className={`${classes.rank} ${isLead ? classes.rankLead : ''}`}>{i + 1}</span>
              <div className={`${classes.mark} ${isDemo ? classes.markActive : ''}`}>
                {player.avatar
                  ? <Avatar photoURL={player.avatar} color={color} size={26} />
                  : <div className={classes.markInner}><RobotIcon color={color} crop="head" /></div>}
              </div>
              <span className={`${classes.name} ${isSelf ? classes.nameSelf : ''}`}>
                {player.username ?? t('game.anonymous')}
              </span>
              {isDemo && <span className={classes.badge}>{t('game.moving')}</span>}
              {!isDemo && player.id === hostId && <span className={classes.badge}>{t('game.host')}</span>}
              {bid && <span className={classes.bid}>{t('game.bidBadge', { count: bid.count })}</span>}
              <span className={`${classes.score} ${isLead ? classes.scoreLead : ''}`}>{score}</span>
              {canKick && !isSelf && (
                <KickButton username={player.username} onKick={() => onKickPlayer(player.id)} />
              )}
            </div>
          );
        })}

        {isLobby && (
          <div className={classes.seat}>
            <span className={classes.rank} />
            <span className={classes.seatRing} />
            <span className={classes.seatLabel}>{t('game.waitingForPlayers')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScoreBoard;
